import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import InventorySubNav from "./components/InventorySubNav";
import { appToast } from "../../../components/AppToast";
import AppDropdown from "../../../components/AppDropdown";
import AppToggle from "../../../components/AppToggle";
import { OrgStructureCheckboxPanel, TaxationCard } from "../../../components/shared-details";
import { getInventoryItemDetail, createInventoryItem, updateInventoryItem } from "./api/inventoryApi";

const ITEM_TYPE_OPTIONS = [
  { value: "Inventory Item", label: "Inventory Item" },
  { value: "Service Item", label: "Service Item" },
  { value: "Fixed Asset", label: "Fixed Asset" },
];
const GROUP_OPTIONS = [
  { value: "Products", label: "Products" },
  { value: "Services", label: "Services" },
  { value: "Raw-material", label: "Raw-material" },
];
const UNIT_OPTIONS = [
  { value: "Numbers", label: "Numbers" },
  { value: "Kg", label: "Kg" },
  { value: "Litre", label: "Litre" },
  { value: "Meter", label: "Meter" },
  { value: "Box", label: "Box" },
  { value: "Pcs", label: "Pcs" },
];
const UOM_EXPIRY_OPTIONS = [
  { value: "Days", label: "Days" },
  { value: "Months", label: "Months" },
  { value: "Years", label: "Years" },
];
const WEIGHT_UNIT_OPTIONS = [
  { value: "gm", label: "gm" },
  { value: "kg", label: "kg" },
  { value: "mg", label: "mg" },
];
const AC_HEAD_OPTIONS = [
  { value: "Select", label: "Select" },
  { value: "Purchase A/c", label: "Purchase A/c" },
  { value: "Sales A/c", label: "Sales A/c" },
  { value: "Direct Expenses", label: "Direct Expenses" },
];
const TAX_HEAD_OPTIONS = [
  { value: "Select", label: "Select" },
  { value: "GST 5%", label: "GST 5%" },
  { value: "GST 12%", label: "GST 12%" },
  { value: "GST 18%", label: "GST 18%" },
  { value: "GST 28%", label: "GST 28%" },
];

function FieldRow({ label, required = false, children }) {
  return (
    <div className="grid grid-cols-12 gap-4 items-center">
      <span className="col-span-4 text-xs font-black uppercase tracking-wider text-on-surface-variant">
        {label}{required && <span className="text-error"> *</span>}
      </span>
      <div className="col-span-8">{children}</div>
    </div>
  );
}

function TextInput({ value, onChange, placeholder = "", type = "text", className = "" }) {
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={`h-10 w-full rounded-xl border border-outline-variant/30 bg-surface-container-low px-4 text-sm text-on-surface outline-none focus:ring-2 focus:ring-primary/20 transition ${className}`}
    />
  );
}

function Card({ title, children }) {
  return (
    <div className="rounded-2xl border border-outline-variant/30 bg-surface-container-lowest overflow-hidden shadow-xs">
      <div className="bg-surface-container-low/60 px-6 py-3 border-b border-outline-variant/25 text-sm font-bold text-on-surface">
        {title}
      </div>
      <div className="p-6 flex flex-col gap-4">{children}</div>
    </div>
  );
}

export default function InventoryEntryScreen() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");
  const isEdit = Boolean(id);

  const [form, setForm] = useState({
    type: "Inventory Item",
    groupName: "Products",
    name: "",
    alias: "",
    upc: "",
    ratePurchase: 0,
    ratePurchaseSale: 0,
    ratePurchaseReadOnly: false,
    mrp: 0,
    brand: "",
    hsnSac: "",
    unit: "Numbers",
    moqPurchase: 0,
    moqSale: 0,
    minStock: 0,
    autoUpdateCost: false,
    length: 0,
    width: 0,
    height: 0,
    weight: 0,
    weightUnit: "gm",
    uomExpiry: "Days",
    uomExpiryQty: 0,
    acHeadPurchase: "Purchase A/c",
    acHeadSale: "Sales A/c",
    defaultTaxHead: "Select",
    customDuty: 0,
    ineligibleCredit: false,
    rcmCalculation: false,
    description: "",
    remarks: "",
    image: null,
    taxType: "Regular",
    taxState: "Maharashtra",
    transporter: "Select",
    pan: "",
    tds: "Select",
    tdsCode: "Select",
    aadhaarNo: "",
    tcs: "0%",
    msmeType: "Select",
    taxCategory: "Select",
    gta: 0,
    gst: "",
    pincode: "",
    distance: 0,
    tan: "",
    tdsRate: 0,
    resident: "Select",
    cinNo: "",
    msmeNo: "",
    currency: "INR",
    ledger: "None",
    companies: [],
    branches: [],
    departments: [],
    status: "Active",
  });

  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(Boolean(id));

  useEffect(() => {
    async function load() {
      if (!id) return;
      setIsLoading(true);
      try {
        const detail = await getInventoryItemDetail(id);
        if (detail) {
          setForm((prev) => ({ ...prev, ...detail }));
        } else {
          appToast.error("Inventory item not found.");
        }
      } catch {
        appToast.error("Failed to load item.");
      } finally {
        setIsLoading(false);
      }
    }
    load();
  }, [id]);

  const patchForm = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleSave = async (options = { exitAfter: true }) => {
    if (!form.name.trim()) {
      appToast.error("Item Name is required.");
      return;
    }
    setIsSaving(true);
    try {
      if (isEdit) {
        await updateInventoryItem(id, { ...form });
        appToast.success("Inventory item updated!");
      } else {
        await createInventoryItem({ ...form });
        appToast.success("Inventory item created!");
      }
      if (options.exitAfter) {
        setTimeout(() => navigate("/masters/inventory/list"), 350);
      } else {
        setForm((prev) => ({
          ...prev,
          name: "",
          alias: "",
          upc: "",
          hsnSac: "",
          brand: "",
          ratePurchase: 0,
          mrp: 0,
          moqPurchase: 0,
          moqSale: 0,
          minStock: 0,
        }));
      }
    } catch {
      appToast.error("Failed to save inventory item.");
    } finally {
      setIsSaving(false);
    }
  };

  const taxationValue = {
    type: form.taxType, state: form.taxState, transporter: form.transporter,
    pan: form.pan, tds: form.tds, tdsCode: form.tdsCode, aadhaarNo: form.aadhaarNo,
    tcs: form.tcs, msmeType: form.msmeType, category: form.taxCategory,
    gta: Boolean(form.gta), gst: form.gst, pincode: form.pincode,
    distance: form.distance, tan: form.tan, tdsRate: form.tdsRate,
    resident: form.resident, cinNo: form.cinNo, msmeNo: form.msmeNo,
    currency: form.currency, ledger: form.ledger,
  };

  const handleTaxationChange = (vals) => {
    setForm((prev) => ({
      ...prev,
      taxType: vals.type || "Regular", taxState: vals.state || "Maharashtra",
      transporter: vals.transporter || "Select", pan: vals.pan || "",
      tds: vals.tds || "Select", tdsCode: vals.tdsCode || "Select",
      aadhaarNo: vals.aadhaarNo || "", tcs: vals.tcs || "0%",
      msmeType: vals.msmeType || "Select", taxCategory: vals.category || "Select",
      gta: vals.gta ? 1 : 0, gst: vals.gst || "", pincode: vals.pincode || "",
      distance: Number(vals.distance || 0), tan: vals.tan || "",
      tdsRate: Number(vals.tdsRate || 0), resident: vals.resident || "Select",
      cinNo: vals.cinNo || "", msmeNo: vals.msmeNo || "",
      currency: vals.currency || "INR", ledger: vals.ledger || "None",
    }));
  };

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-6 pb-16">
      {/* Header */}
      <section className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.16em] text-primary">Inventory Master</p>
          <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tight text-on-surface">
            {isEdit ? "Edit Inventory Item" : "Create Inventory Item"}
          </h1>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <InventorySubNav />
          <Link
            to="/masters/inventory/list"
            className="inline-flex items-center gap-1 text-xs font-bold text-on-surface-variant hover:text-primary transition"
          >
            <span className="material-symbols-outlined text-base">arrow_back</span>
            <span>Back</span>
          </Link>
        </div>
      </section>

      {isLoading ? (
        <div className="py-20 text-center text-sm text-on-surface-variant font-bold">
          Loading item details...
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
            {/* LEFT COLUMN */}
            <div className="flex flex-col gap-6">
              <Card title="Item Details">
                <FieldRow label="Type" required>
                  <AppDropdown options={ITEM_TYPE_OPTIONS} value={form.type} onChange={(val) => patchForm("type", val)} triggerClassName="h-10 bg-surface-container-low" />
                </FieldRow>
                <FieldRow label="Group" required>
                  <AppDropdown options={GROUP_OPTIONS} value={form.groupName} onChange={(val) => patchForm("groupName", val)} triggerClassName="h-10 bg-surface-container-low" />
                </FieldRow>
                <FieldRow label="Name" required>
                  <TextInput value={form.name} onChange={(val) => patchForm("name", val)} placeholder="Item name..." />
                </FieldRow>

                {/* Alias + UPC */}
                <div className="grid grid-cols-12 gap-4 items-center">
                  <span className="col-span-4 text-xs font-black uppercase tracking-wider text-on-surface-variant">Alias</span>
                  <div className="col-span-8 grid grid-cols-2 gap-3">
                    <TextInput value={form.alias} onChange={(val) => patchForm("alias", val)} placeholder="Alias..." />
                    <div className="flex flex-col gap-1">
                      <span className="text-[10px] font-black uppercase tracking-wider text-on-surface-variant">UPC</span>
                      <TextInput value={form.upc} onChange={(val) => patchForm("upc", val)} placeholder="Barcode..." />
                    </div>
                  </div>
                </div>

                {/* Rate Purchase + Sale */}
                <div className="grid grid-cols-12 gap-4 items-center">
                  <span className="col-span-4 text-xs font-black uppercase tracking-wider text-on-surface-variant">Rate (Purchase)</span>
                  <div className="col-span-8 grid grid-cols-2 gap-3 items-end">
                    <TextInput type="number" value={form.ratePurchase} onChange={(val) => patchForm("ratePurchase", parseFloat(val) || 0)} />
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] font-black uppercase tracking-wider text-on-surface-variant">(Sale)</span>
                        <label className="flex items-center gap-1.5 cursor-pointer select-none">
                          <input
                            type="checkbox"
                            checked={form.ratePurchaseReadOnly}
                            onChange={(e) => patchForm("ratePurchaseReadOnly", e.target.checked)}
                            className="rounded border-outline-variant/50 text-primary h-3.5 w-3.5 cursor-pointer"
                          />
                          <span className="text-[10px] text-on-surface-variant font-medium">Read only</span>
                        </label>
                      </div>
                      <TextInput
                        type="number"
                        value={form.ratePurchaseSale}
                        onChange={(val) => patchForm("ratePurchaseSale", parseFloat(val) || 0)}
                        className={form.ratePurchaseReadOnly ? "opacity-50 pointer-events-none" : ""}
                      />
                    </div>
                  </div>
                </div>

                {/* MRP + Brand */}
                <div className="grid grid-cols-12 gap-4 items-center">
                  <span className="col-span-4 text-xs font-black uppercase tracking-wider text-on-surface-variant">MRP</span>
                  <div className="col-span-8 grid grid-cols-2 gap-3 items-end">
                    <TextInput type="number" value={form.mrp} onChange={(val) => patchForm("mrp", parseFloat(val) || 0)} />
                    <div className="flex flex-col gap-1">
                      <span className="text-[10px] font-black uppercase tracking-wider text-on-surface-variant">Brand</span>
                      <TextInput value={form.brand} onChange={(val) => patchForm("brand", val)} placeholder="Brand..." />
                    </div>
                  </div>
                </div>

                <FieldRow label="HSN/SAC">
                  <TextInput value={form.hsnSac} onChange={(val) => patchForm("hsnSac", val)} placeholder="e.g. 4562, 711319" />
                </FieldRow>
                <FieldRow label="Unit">
                  <AppDropdown options={UNIT_OPTIONS} value={form.unit} onChange={(val) => patchForm("unit", val)} triggerClassName="h-10 bg-surface-container-low" />
                </FieldRow>

                {/* MOQ Purchase + Min Stock */}
                <div className="grid grid-cols-12 gap-4 items-center">
                  <span className="col-span-4 text-xs font-black uppercase tracking-wider text-on-surface-variant">MOQ (Purchase)</span>
                  <div className="col-span-8 grid grid-cols-2 gap-3 items-end">
                    <TextInput type="number" value={form.moqPurchase} onChange={(val) => patchForm("moqPurchase", parseFloat(val) || 0)} />
                    <div className="flex flex-col gap-1">
                      <span className="text-[10px] font-black uppercase tracking-wider text-on-surface-variant">Min. Stock</span>
                      <TextInput type="number" value={form.minStock} onChange={(val) => patchForm("minStock", parseFloat(val) || 0)} />
                    </div>
                  </div>
                </div>

                {/* MOQ Sale */}
                <div className="grid grid-cols-12 gap-4 items-center">
                  <span className="col-span-4 text-xs font-black uppercase tracking-wider text-on-surface-variant">MOQ (Sale)</span>
                  <div className="col-span-8 grid grid-cols-2 gap-3">
                    <TextInput type="number" value={form.moqSale} onChange={(val) => patchForm("moqSale", parseFloat(val) || 0)} />
                  </div>
                </div>

                {/* Auto-update Cost */}
                <div className="grid grid-cols-12 gap-4 items-center">
                  <span className="col-span-4 text-xs font-black uppercase tracking-wider text-on-surface-variant">Auto-update Cost</span>
                  <div className="col-span-8 flex items-center h-10">
                    <AppToggle checked={form.autoUpdateCost} onChange={(val) => patchForm("autoUpdateCost", val)} label="Based on last purchase" />
                  </div>
                </div>
              </Card>

              {/* Dimensions Card */}
              <Card title="Dimensions &amp; Expiry">
                <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                  <FieldRow label="Length">
                    <TextInput type="number" value={form.length} onChange={(val) => patchForm("length", parseFloat(val) || 0)} />
                  </FieldRow>
                  <FieldRow label="Width">
                    <TextInput type="number" value={form.width} onChange={(val) => patchForm("width", parseFloat(val) || 0)} />
                  </FieldRow>
                  <FieldRow label="Height">
                    <TextInput type="number" value={form.height} onChange={(val) => patchForm("height", parseFloat(val) || 0)} />
                  </FieldRow>
                  <div className="grid grid-cols-12 gap-2 items-center">
                    <span className="col-span-4 text-xs font-black uppercase tracking-wider text-on-surface-variant">Weight</span>
                    <div className="col-span-8 flex items-center gap-2">
                      <TextInput type="number" value={form.weight} onChange={(val) => patchForm("weight", parseFloat(val) || 0)} className="flex-1" />
                      <AppDropdown
                        options={WEIGHT_UNIT_OPTIONS}
                        value={form.weightUnit}
                        onChange={(val) => patchForm("weightUnit", val)}
                        className="w-20 shrink-0"
                        triggerClassName="h-10 bg-surface-container-low text-xs"
                      />
                    </div>
                  </div>
                  <FieldRow label="UOM Expiry">
                    <AppDropdown options={UOM_EXPIRY_OPTIONS} value={form.uomExpiry} onChange={(val) => patchForm("uomExpiry", val)} triggerClassName="h-10 bg-surface-container-low" />
                  </FieldRow>
                  <FieldRow label="Expiry Qty">
                    <TextInput type="number" value={form.uomExpiryQty} onChange={(val) => patchForm("uomExpiryQty", parseFloat(val) || 0)} />
                  </FieldRow>
                </div>
              </Card>
            </div>

            {/* RIGHT COLUMN */}
            <div className="flex flex-col gap-6">
              <Card title="Accounting &amp; Tax">
                <FieldRow label="A/c Head Purchase" required>
                  <AppDropdown options={AC_HEAD_OPTIONS} value={form.acHeadPurchase} onChange={(val) => patchForm("acHeadPurchase", val)} triggerClassName="h-10 bg-surface-container-low" />
                </FieldRow>
                <FieldRow label="A/c Head Sale" required>
                  <AppDropdown options={AC_HEAD_OPTIONS} value={form.acHeadSale} onChange={(val) => patchForm("acHeadSale", val)} triggerClassName="h-10 bg-surface-container-low" />
                </FieldRow>
                <FieldRow label="Default Tax Head">
                  <AppDropdown options={TAX_HEAD_OPTIONS} value={form.defaultTaxHead} onChange={(val) => patchForm("defaultTaxHead", val)} triggerClassName="h-10 bg-surface-container-low" />
                </FieldRow>
                <FieldRow label="Custom Duty">
                  <TextInput type="number" value={form.customDuty} onChange={(val) => patchForm("customDuty", parseFloat(val) || 0)} />
                </FieldRow>

                {/* Ineligible Credit + RCM */}
                <div className="grid grid-cols-12 gap-4 items-center">
                  <span className="col-span-4 text-xs font-black uppercase tracking-wider text-on-surface-variant">Ineligible Credit</span>
                  <div className="col-span-8 grid grid-cols-2 gap-3 items-center">
                    <div className="flex items-center h-10">
                      <AppToggle checked={form.ineligibleCredit} onChange={(val) => patchForm("ineligibleCredit", val)} />
                    </div>
                    <div className="flex flex-col gap-1">
                      <span className="text-[10px] font-black uppercase tracking-wider text-on-surface-variant">RCM Calculation</span>
                      <div className="flex items-center h-10">
                        <AppToggle checked={form.rcmCalculation} onChange={(val) => patchForm("rcmCalculation", val)} />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Description */}
                <label className="grid grid-cols-12 gap-4 items-start">
                  <span className="col-span-4 text-xs font-black uppercase tracking-wider text-on-surface-variant pt-2.5">Description</span>
                  <textarea
                    value={form.description}
                    onChange={(e) => patchForm("description", e.target.value)}
                    placeholder="Item description..."
                    rows={3}
                    className="col-span-8 rounded-xl border border-outline-variant/30 bg-surface-container-low p-3.5 text-sm text-on-surface outline-none focus:ring-2 focus:ring-primary/20 transition resize-y"
                  />
                </label>

                {/* Remarks */}
                <label className="grid grid-cols-12 gap-4 items-start">
                  <span className="col-span-4 text-xs font-black uppercase tracking-wider text-on-surface-variant pt-2.5">Remarks</span>
                  <textarea
                    value={form.remarks}
                    onChange={(e) => patchForm("remarks", e.target.value)}
                    placeholder="Remarks..."
                    rows={2}
                    className="col-span-8 rounded-xl border border-outline-variant/30 bg-surface-container-low p-3.5 text-sm text-on-surface outline-none focus:ring-2 focus:ring-primary/20 transition resize-y"
                  />
                </label>
              </Card>

              {/* Item Image */}
              <Card title="Item Image">
                <div className="flex flex-col items-center gap-4">
                  <div className="w-full aspect-video rounded-xl border-2 border-dashed border-outline-variant/40 bg-surface-container-low flex flex-col items-center justify-center gap-2 cursor-pointer hover:border-primary/40 hover:bg-surface-container transition select-none overflow-hidden">
                    {form.image ? (
                      <img src={form.image} alt="Item" className="w-full h-full object-cover" />
                    ) : (
                      <>
                        <span className="material-symbols-outlined text-4xl text-on-surface-variant">image</span>
                        <span className="text-xs text-on-surface-variant font-medium">No image selected</span>
                      </>
                    )}
                  </div>
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          const reader = new FileReader();
                          reader.onload = (ev) => patchForm("image", ev.target.result);
                          reader.readAsDataURL(file);
                        }
                      }}
                    />
                    <span className="inline-flex items-center gap-2 rounded-xl border border-outline-variant/40 bg-surface-container-low px-5 py-2.5 text-xs font-bold text-on-surface hover:bg-surface-container transition">
                      <span className="material-symbols-outlined text-base">upload</span>
                      Choose File
                    </span>
                  </label>
                </div>
              </Card>

              {/* Taxation Card (shared component) */}
              <TaxationCard
                value={taxationValue}
                onChange={handleTaxationChange}
                onGstVerifyClick={() => appToast.info("Verifying GST details...")}
              />
            </div>
          </div>

          {/* Organization Scope */}
          <section className="rounded-3xl border border-outline-variant/30 bg-surface-container-lowest p-6 shadow-sm">
            <div className="border-b border-outline-variant/20 pb-4 mb-6">
              <h2 className="font-display text-xl font-bold text-on-surface">Organization Scope</h2>
              <p className="text-xs text-on-surface-variant mt-1">
                Assign the Companies, Branches, and Departments where this item is available.
              </p>
            </div>
            <OrgStructureCheckboxPanel
              selectedCompanies={form.companies}
              selectedBranches={form.branches}
              selectedDepartments={form.departments}
              onCompaniesChange={(val) => patchForm("companies", val)}
              onBranchesChange={(val) => patchForm("branches", val)}
              onDepartmentsChange={(val) => patchForm("departments", val)}
            />
          </section>

          {/* Footer Actions */}
          <section className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-outline-variant/20 bg-surface-container-lowest p-4 shadow-sm">
            <button
              type="button"
              onClick={() => appToast.info("Audit history tracking is enabled.")}
              className="inline-flex items-center gap-1.5 rounded-xl bg-surface-container-low px-4 py-2.5 text-xs font-bold text-on-surface-variant transition hover:bg-surface-container-high"
            >
              <span className="material-symbols-outlined text-sm">history</span>
              <span>History</span>
            </button>
            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => navigate("/masters/inventory/list")}
                className="rounded-xl bg-secondary-container px-6 py-2.5 text-xs font-bold text-on-secondary-container transition hover:brightness-95 cursor-pointer"
              >
                Cancel
              </button>
              {!isEdit && (
                <button
                  type="button"
                  disabled={isSaving}
                  onClick={() => handleSave({ exitAfter: false })}
                  className="rounded-xl border border-outline-variant/40 bg-surface-container-lowest px-6 py-2.5 text-xs font-bold text-primary transition hover:bg-surface-container-low disabled:cursor-not-allowed disabled:opacity-60 cursor-pointer"
                >
                  Save &amp; Continue
                </button>
              )}
              <button
                type="button"
                disabled={isSaving}
                onClick={() => handleSave({ exitAfter: true })}
                className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-br from-primary to-primary-container px-8 py-2.5 text-xs font-bold text-on-primary shadow-lg shadow-primary/20 transition hover:brightness-105 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60 cursor-pointer"
              >
                <span className="material-symbols-outlined text-base">save</span>
                <span>{isSaving ? "Saving..." : "Save & Exit"}</span>
              </button>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
